import type { AdminSessionResult } from '../api/adminSession';

export type AdminGuardDecision = 'loading' | 'sign-in' | 'offline' | 'error' | 'not-available' | 'step-up' | 'allow';

export interface AdminGuardInputs {
  hasSession: boolean;
  sessionLoading: boolean;
  adminSessionLoading: boolean;
  /** `useAdminSession().isError` — fix round 1, item 4: without this, a
   * failed query (network blip, 500, CORS) left `adminSession` permanently
   * `undefined` while `adminSessionLoading` was `false`, which the old
   * decision logic couldn't distinguish from "still loading" and rendered
   * "Loading…" forever with no way out. */
  adminSessionError: boolean;
  /** `useAdminSession().fetchStatus` — fix round 2 (minor): TanStack
   * Query's default `networkMode: 'online'` PAUSES a fetch while the
   * browser is offline rather than letting it fail — `fetchStatus` is
   * `'paused'`, `status` stays `'pending'`. Without checking this
   * separately, an offline first load was indistinguishable from a genuine
   * server error (or, depending on timing, stuck on "Loading…" forever) —
   * neither message ("couldn't load your account, retry?" / a bare
   * spinner) tells a visitor with no signal what's actually going on or
   * that retrying won't help until connectivity returns. */
  adminSessionFetchStatus: 'fetching' | 'paused' | 'idle';
  adminSession: AdminSessionResult | undefined;
}

/**
 * The admin root guard's allow/deny decision (spec §3: "admin root guard
 * requires AAL2 and a server probe"; task-10 brief: "403 → flat 'Not
 * available'"). Pure — no React, no network — so it's exhaustively
 * Vitest-testable without a running Supabase instance, mirroring how
 * `supabase/functions/_shared/admin_lib.ts` keeps `decideAdminAccess` pure
 * server-side. The component wrapping this only resolves the network facts
 * (session + `useAdminSession()`) and renders whatever this returns.
 *
 * Deliberately does NOT branch on FOUNDER_EMAILS or any client-side
 * allowlist — `admin-session`'s `isAdmin` (server truth) is the only input
 * that can produce `allow` (plan: "FOUNDER_EMAILS is deliberately NOT
 * ported — server truth is the only gate").
 */
export function decideAdminGuard({
  hasSession,
  sessionLoading,
  adminSessionLoading,
  adminSessionError,
  adminSessionFetchStatus,
  adminSession,
}: AdminGuardInputs): AdminGuardDecision {
  if (sessionLoading) return 'loading';
  if (!hasSession) return 'sign-in';
  // Checked before 'loading'/'error': a paused fetch with no cached answer
  // yet is neither — it's waiting on the network, not broken. If cached
  // data already exists (adminSession !== undefined), fall through and
  // serve it — a background refetch pausing offline shouldn't interrupt an
  // already-resolved session.
  if (adminSessionFetchStatus === 'paused' && adminSession === undefined) return 'offline';
  if (adminSessionLoading) return 'loading';
  if (adminSessionError || adminSession === undefined) return 'error';
  if (!adminSession.isAdmin) return 'not-available';
  if (adminSession.aal !== 'aal2') return 'step-up';
  return 'allow';
}
